import { cn } from '@/lib/cn';
import type { PondOverview, AlertThresholds } from '@/types/api';
import type { StatusColor } from '@/lib/paramColor';
import { DayReadingsTable } from './DayReadingsTable';

interface FarmMapViewProps {
  ponds: PondOverview[];
  alertThresholds?: AlertThresholds | null;
  onPondClick: (pond: PondOverview) => void;
}

const DOT_COLORS: Record<StatusColor, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  gray: 'bg-gray-300',
};

const BORDER_COLORS: Record<StatusColor, string> = {
  green: 'border-t-green-500',
  amber: 'border-t-amber-500',
  red: 'border-t-red-500',
  gray: 'border-t-gray-300',
};

function getPondStatus(pond: PondOverview): StatusColor {
  if (pond.status !== 'active') return 'gray';
  const alertCount = pond.alert_count ?? 0;
  if (alertCount > 0) return 'red';
  if (pond.active_cycle?.latest_reading?.has_alert) return 'amber';
  return 'green';
}

const DOT_TEXT: Partial<Record<StatusColor, string>> = { amber: 'warning', red: 'critical' };

function StatusDot({ color }: { color: StatusColor }) {
  return (
    <span className={cn('inline-block w-2 h-2 rounded-full shrink-0', DOT_COLORS[color])}>
      {DOT_TEXT[color] && <span className="sr-only">{DOT_TEXT[color]}</span>}
    </span>
  );
}

function PondMapCard({
  pond,
  alertThresholds,
  onClick,
}: {
  pond: PondOverview;
  alertThresholds?: AlertThresholds | null;
  onClick: () => void;
}) {
  const status = getPondStatus(pond);
  const cycle = pond.active_cycle;
  const isIdle = pond.status !== 'active' || !cycle;

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full flex-col justify-start text-left rounded-xl border bg-white shadow-sm hover:shadow-md transition-all',
        'border-t-4',
        BORDER_COLORS[status],
        isIdle && 'opacity-60'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{pond.name}</h3>
          {cycle && (
            <span className="text-xs text-gray-500 shrink-0">DOC {cycle.doc}</span>
          )}
        </div>
        <StatusDot color={status} />
      </div>

      {isIdle ? (
        <div className="px-3 pb-3">
          <span className="text-xs text-gray-400 italic">{pond.status === 'preparing' ? 'Preparing' : 'Idle'}</span>
        </div>
      ) : (
        <div className="px-3 pb-3">
          {cycle?.day_readings && <DayReadingsTable day={cycle.day_readings} alertThresholds={alertThresholds} />}
        </div>
      )}
    </button>
  );
}

export function FarmMapView({ ponds, alertThresholds, onPondClick }: FarmMapViewProps) {
  // Sort: always alphabetical A-Z
  const sortedPonds = [...ponds].sort((a, b) => a.name.localeCompare(b.name));

  const activePonds = sortedPonds.filter(p => p.status === 'active');
  const preparingPonds = sortedPonds.filter(p => p.status === 'preparing');
  const idlePonds = sortedPonds.filter(p => p.status !== 'active' && p.status !== 'preparing');
  const alertCount = ponds.reduce((sum, p) => sum + (p.alert_count ?? 0), 0);

  return (
    <div className="space-y-4">
      {/* Pond Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {sortedPonds.map((pond) => (
          <PondMapCard
            key={pond.id}
            pond={pond}
            alertThresholds={alertThresholds}
            onClick={() => onPondClick(pond)}
          />
        ))}
      </div>

      {/* Summary Bar */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-600">
        <span>
          Total Ponds: <span className="font-semibold text-gray-900">{ponds.length}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span>
          Active: <span className="font-semibold text-gray-900">{activePonds.length}</span>
        </span>
        {preparingPonds.length > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <span>
              Preparing: <span className="font-semibold text-gray-900">{preparingPonds.length}</span>
            </span>
          </>
        )}
        <span className="text-gray-300">|</span>
        <span>
          Idle: <span className="font-semibold text-gray-900">{idlePonds.length}</span>
        </span>
        {alertCount > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <span className="text-red-600">
              Alerts: <span className="font-semibold">{alertCount}</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
}
